import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IUnitCapRuleset,
  IUnitCapRulesetCreateRequest,
  IUnitCapRulesetUpdateRequest,
} from "types/UnitCap/UnitCap";

import { api } from ".";

interface IListResponse {
  data: IUnitCapRuleset[];
  message?: string;
}

interface IItemResponse {
  data: IUnitCapRuleset;
  message?: string;
}

const unitCapQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllUnitCap: builder.query<
      IListResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `unit-cap-rulesets/all${search}`;
      },
      providesTags: ["UnitCapRulesets"],
    }),
    getUnitCapById: builder.query<IItemResponse, string>({
      query: (id) => `unit-cap-rulesets/${id}`,
      providesTags: (_r, _e, id) => [{ type: "UnitCapRulesets", id }],
    }),
    createUnitCap: builder.mutation<
      IItemResponse,
      IUnitCapRulesetCreateRequest
    >({
      query: (body) => ({
        url: "unit-cap-rulesets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UnitCapRulesets"],
    }),
    updateUnitCap: builder.mutation<
      IItemResponse,
      IUnitCapRulesetUpdateRequest
    >({
      query: (body) => ({
        url: `unit-cap-rulesets/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["UnitCapRulesets", "DefaultRulePropagation"],
    }),
    deleteUnitCap: builder.mutation<void, string>({
      query: (id) => ({
        url: `unit-cap-rulesets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UnitCapRulesets", "DefaultRulePropagation"],
    }),
    cloneUnitCapAsDefault: builder.mutation<IItemResponse, string>({
      query: (id) => ({
        url: `unit-cap-rulesets/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["UnitCapRulesets"],
    }),
    copyUnitCapToContract: builder.mutation<
      IItemResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `unit-cap-rulesets/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["UnitCapRulesets", "ContractFeeSchedules"],
    }),
    getUnitCapPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `unit-cap-rulesets/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `unitCap:${id}` },
      ],
    }),
    applyUnitCapPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `unit-cap-rulesets/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "UnitCapRulesets",
        { type: "DefaultRulePropagation", id: `unitCap:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllUnitCapQuery,
  useGetUnitCapByIdQuery,
  useCreateUnitCapMutation,
  useUpdateUnitCapMutation,
  useDeleteUnitCapMutation,
  useCloneUnitCapAsDefaultMutation,
  useCopyUnitCapToContractMutation,
  useGetUnitCapPropagationPreviewQuery,
  useLazyGetUnitCapPropagationPreviewQuery,
  useApplyUnitCapPropagationMutation,
} = unitCapQueries;
